import { Event, PrecisionManufacturing } from '@mui/icons-material';
import { Alert, AlertTitle, Divider, List, ListItem, ListItemIcon, ListItemText, Stack, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import React, { useCallback, useEffect } from "react";
import { FormattedNumber } from "react-intl";
import { Link, useNavigate } from "react-router-dom";
import { useGetV4WarehousesQuery, usePutV4ManufacturerByRouteManufacturerIdInvoiceAndReceiptNumberMutation } from "randmar-api-client";
import { Card, useToast } from "randmarcomps";
import withManufacturer from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";
import { Receipt } from "../../stores/document/Document";
import CommentsView from "../comments/CommentsView";
import ProductDetailsModal from "../products/ProductDetailsModal";
import CurrencyUtils from "../util/CurrencyUtils";
import FormatDateFromInt from "../util/DateTimeUtil";
import InvoicePanel from "../util/InvoicePanel";
import LoadingComponent from "../util/LoadingComponent";
import TotalTable from "../util/TotalTable";

interface IProps {
  receipt: Receipt;
  applicationId: string;
}

const ReceiptView: React.FC<IProps> = ({ receipt, applicationId }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [insertInvoice, { isLoading }] = usePutV4ManufacturerByRouteManufacturerIdInvoiceAndReceiptNumberMutation();
  const { data: warehouses } = useGetV4WarehousesQuery();


  // The original component had navigation logic inside the render method.
  // This is a side effect and should be handled by useEffect in a functional component.
  useEffect(() => {
    if (!receipt.partNumbers) {
      navigate(`/${applicationId}/404`);
    }
  }, [receipt.partNumbers, applicationId, navigate]);

  const getWarehouseNameFromCode = useCallback((code?: string) => {
    if (!code || !warehouses) {
      return "B/O";
    }
    const warehouse = warehouses.find(w => w.WarehouseId === code);
    return warehouse?.Name || "B/O";
  }, [warehouses]);

  const handleConfirm = async (InvoiceNumber: string, InvoiceDate: string, InvoiceTotal: number) => {
    try {
      await insertInvoice({
        routeManufacturerId: applicationId,
        receiptNumber: receipt.receiptNumber || "",
        invoiceToReceiptInput: {
          InvoiceNumber,
          InvoiceDate,
          InvoiceTotal,
        }
      }).unwrap();
      toast({ variant: "success", title: "Success", description: "Receipt successfully invoiced." });
    } catch (e) {
      toast({ variant: "destructive", title: "Error", description: "Could not invoice this receipt" });
    }
  };

  if (isLoading) {
    return <LoadingComponent text="Awaiting response" />;
  }

  // If the receipt has no parts, we are navigating away, so render nothing.
  if (!receipt.partNumbers) {
    return null;
  }

  return (
    <>
      <Stack spacing={2} sx={{ mb: 2, width: "100%" }} direction={{ xs: "column", md: "row" }} justifyContent="space-between">
        <Card className="w-full p-4 md:w-1/2">
          <h4 className="mx-5 mb-1 mt-2.5">History</h4>
          <CommentsView comments={receipt.comments || ""} />
          <Divider sx={{ my: 3, width: '100%' }} />
          <h4 className="mx-5 mb-1 mt-2.5">Accounting Information</h4>
          <TotalTable
            subTotal={receipt.subTotal || 0}
            taxAmount1={receipt.taxAmount1 || 0}
            taxAmount2={receipt.taxAmount2 || 0}
            total={receipt.total || 0}
          />
        </Card>
        <Card className="w-full p-4">
          <h4 className="mx-5 mb-1 mt-2.5">Reception Information</h4>
          <Alert
            severity="info"
            action={!receipt.invoiced &&
              <InvoicePanel name="Add Invoice" onConfirm={handleConfirm} />
            }
          >
            <AlertTitle color="#3b6cca">Reception Invoicing</AlertTitle>
            {receipt.invoiceNumber
              ? receipt.invoiceNumber
              : "This reception has not been invoiced. You can add your invoice through the button on the right."
            }
          </Alert>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="flex-end" alignItems="center" sx={{ mt: 2 }} spacing={1}>
            <List dense disablePadding sx={{ width: '100%', bgcolor: 'background.paper' }}>
              <ListItem sx={{ "&:hover": { backgroundColor: "#f9f5ff" }, borderRadius: "20px" }}>
                <ListItemIcon><Event /></ListItemIcon>
                <ListItemText primary="Reception Date" secondary={FormatDateFromInt(receipt.receiptDate)} />
              </ListItem>
              <ListItem sx={{ "&:hover": { backgroundColor: "#f9f5ff" }, borderRadius: "20px" }}>
                <ListItemIcon><PrecisionManufacturing /></ListItemIcon>
                <ListItemText primary="Location" secondary={getWarehouseNameFromCode(receipt.location)} />
              </ListItem>
              <ListItem sx={{ "&:hover": { backgroundColor: "#f9f5ff" }, borderRadius: "20px" }}>
                <ListItemIcon><PrecisionManufacturing /></ListItemIcon>
                <ListItemText primary="Purchase Order Number" secondary={<Link to={Path.document(applicationId, receipt.purchaseOrderNumber)}>{receipt.purchaseOrderNumber}</Link>} />
              </ListItem>
            </List>
          </Stack>
        </Card>
      </Stack>
      <Card className="w-full p-4 mb-4">
        <h4 className="mx-5 mb-1 mt-2.5">Part Numbers Received ({(receipt.partNumbers)?.length})</h4>
        <div style={{ borderRadius: '20px', width: '100%', overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f9f5ff" }}>
                <TableCell align="center" sx={{ borderColor: "#582c83" }}>Product</TableCell>
                <TableCell align="center" sx={{ borderColor: "#582c83" }}>Quantity</TableCell>
                <TableCell align="center" sx={{ borderColor: "#582c83" }}>Unit Price</TableCell>
                <TableCell align="center" sx={{ borderColor: "#582c83" }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {receipt.partNumbers
                .slice()
                .sort((a, b) => ((a.mpn || "").localeCompare(b.mpn || "")))
                .map(pn =>
                  <TableRow sx={{ "&:hover": { backgroundColor: "#f9f5ff" } }} key={"partNumber" + pn.mpn}>
                    <TableCell>
                      <List dense disablePadding sx={{ width: '100%' }}>
                        <ListItem>
                          <ListItemText
                            primary={
                              <Link to={Path.product(applicationId, pn.randmarSKU)}>
                                {pn.mpn}
                              </Link>
                            }
                            secondary={pn.description} />
                        </ListItem>
                      </List>
                    </TableCell>
                    <TableCell align="center">{pn.quantity}</TableCell>
                    <TableCell align="center"><FormattedNumber {...CurrencyUtils.format} value={pn.unitPrice || 0} /></TableCell>
                    <TableCell align="center"><ProductDetailsModal randmarSKU={pn.randmarSKU || ""} /></TableCell>
                  </TableRow>
                )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </>
  );
};

export default withManufacturer(ReceiptView);
